import {
  initializeDecelerationPath,
  currentDecelerationPathPosition,
  // isDecelerationAnimationFinished,
} from  './DecelerationAnimator';

export const CENTER_NODE_DIAMETER = 720;
export const EXPANDED_NODE_DIAMETER = 300;
export const ICON_NODE_DIAMETER = 55;
export const CENTER_NODE_RADIUS = CENTER_NODE_DIAMETER / 2;
export const EXPANDED_NODE_RADIUS = EXPANDED_NODE_DIAMETER / 2;
export const ICON_NODE_RADIUS = ICON_NODE_DIAMETER / 2;
export const CENTER_NAME = 'center';

const CENTER_SELECTED_X = 200;
const SELECTED_RADII = [0, 1300, 500, 250];
const CENTER_RADII_OFFSET = SELECTED_RADII.slice(1, -1);
const CENTER_RADII_ROTATED_OFFSET = CENTER_RADII_OFFSET.map((x, index) => index === 0 ? x : x * 0.5);
const DISPLAYING_FULL_SIZE_RADII = [0, 600, 350, 250];
const PARENT_DISPLAYING_FULL_SIZE_RADII = [0, 600, 300, 180];
const UNSELECTED_RADII = [0, 600, 170, 50];

const ANIMATE_X_OFFSET_TIME = 1.5;
const ANIMATE_RADIUS_TIME = 1;
const DECELERATE_RADIUS_TIME = 0.33;
const CENTER_ANIMATE_ANGLE_TIME = 2.5;
const ANIMATE_ANGLE_TIME = 1.3;
const DECELERATE_ANGLE_TIME = 0.75;

const BASE_SCREEN_HALF_HEIGHT = 500;
const D_EYE_TO_SCREEN = 1000;
const BASE_Z = 800;
const IWHEEL_PLANE_ANGLE = -45 / 360 * Math.PI * 2;

export const nodeBody = nodeData => isCenter(nodeData) ? nodeData.text : `${nodeData.name}. ${nodeData.text}`;
export const nodeExtendedBody = nodeData => {
  let text = nodeBody(nodeData);

  if (nodeData.details) {
    text = `${text}\n\nDetails: ${nodeData.details}`;
  }

  if (nodeData.hasScore) {
    text = `${text}\n\nScore: ${scoreString({ hasScore: nodeData.hasScore, desirability: nodeData.desirability, likelihood: nodeData.likelihood })}`;
  }

  if (nodeData.minorityReport) {
    text = `${text}\n\nMinority Report: ${nodeData.minorityReport}`;
  }

  if (nodeData.hasComparisonScore) {
    text = `${text}\n\nComparison Score: ${scoreString({ hasScore: nodeData.hasComparisonScore, desirability: nodeData.comparisonDesirability, likelihood: nodeData.comparisonLikelihood })}`;
  }

  if (nodeData.comparisonMinorityReport) {
    text = `${text}\n\nComparison Minority Report: ${nodeData.comparisonMinorityReport}`;
  }

  if (nodeData.delay) {
    text = `${text}\n\nDelay: ${nodeData.delay}`;

    if (nodeData.delayComments) {
      text = `${text}\n${nodeData.delay}`;
    }
  }

  if (nodeData.cumulativeDelay) {
    text = `${text}\n\nCumulative Delay: ${nodeData.cumulativeDelay}`;
  }

  if (nodeData.decisionStrategyType) {
    text = `${text}\n\n${nodeData.decisionStrategyType}: ${nodeData.decisionStrategy}`;
  }

  if (nodeData.tags) {
    text = `${text}\n\nTags: ${nodeData.tags}`;
  }

  // if (nodeData.author) {
  //   text = `${text}\n\nAuthor: ${nodeData.author}`;
  // }

  return text;
};

function scoreString ({ hasScore, desirability, likelihood }) {
  if (!hasScore) return 'unscored';
  let desirabilityString = `${desirability}`;
  if (!likelihood) return desirabilityString;
  return `${desirabilityString} / ${likelihood}`;
}

function firstOrderAncestorNode ({ nodeData, iWheelExploration }) {
  if (isCenter(nodeData)) return null;
  if (nodeData.order === 1) return nodeData;
  return findByName({ nodeData: iWheelExploration, name: firstOrderAncestorName(nodeData) });
}
const findByName = ( { nodeData, name }) => nodeData.name === name ? nodeData : nodeData.children.reduce((child, match) => match || findByName({ nodeData: child, name }, null));

function cumulativeRadii ({ selectedNode, iWheelExploration }) {
  const firstOrderAncestor = firstOrderAncestorNode({ nodeData: selectedNode, iWheelExploration });
  return (firstOrderAncestor?.avoidLineAngleOffset ? CENTER_RADII_ROTATED_OFFSET : CENTER_RADII_OFFSET).slice(0, selectedNode.order).reduce((accumulator, sum) => sum + accumulator, 0);
}

export function selectNode ({ selectedNode, iWheelExploration }) {
  iWheelExploration = animateCenterX({ nodeData: iWheelExploration, targetX: CENTER_SELECTED_X - cumulativeRadii({ selectedNode, iWheelExploration }) * 0.75 });
  iWheelExploration = setRadii({ nodeData: iWheelExploration, selectedNode });
  iWheelExploration = setAngles({ nodeData: iWheelExploration, selectedNode });

  return iWheelExploration;
}

const animateCenterX = ({ nodeData, targetX, startingX=currentDecelerationPathPosition(nodeData.xAnimator) }) => (
  {
    ...nodeData,
    xAnimator: initializeDecelerationPath({ initialValue: startingX, finalValue: targetX, runSeconds: ANIMATE_X_OFFSET_TIME, decelerationSeconds: DECELERATE_RADIUS_TIME }),
  }
);

const setRadii = ({ nodeData, selectedNode }) => (
  {
    ...nodeData,
    radiusToParentAnimator: initializeDecelerationPath({
      initialValue: currentDecelerationPathPosition(nodeData.radiusToParentAnimator),
      finalValue: targetRadius({ nodeData, selectedNode }),
      runSeconds: ANIMATE_RADIUS_TIME,
      decelerationSeconds: DECELERATE_RADIUS_TIME,
    }),
    children: nodeData.children.map(child => setRadii({ nodeData: child, selectedNode }))
  }
);

const angleBetweenNodesForMyChildren = nodeData => Math.PI * 2.0 / nodeData.children.length;

const indexOfSelectedChild = ({ children, selectedNode}) => {
  const child = children.find(nodeData => descendantSelected({ nodeData, selectedNodeName: selectedNode.name }));

  if (child) {
    return child.index;
  } else {
    return false;
  }
};

function setAngles ({ nodeData, selectedNode }) {
  const currentAngle = currentDecelerationPathPosition(nodeData.angleAnimator) % (2 * Math.PI) + 2 * Math.PI;
  const angleBetweenNodes = angleBetweenNodesForMyChildren(nodeData);

  if (isCenter(selectedNode)) {
    return {
      ...nodeData,
      angleAnimator: initializeDecelerationPath({
        initialValue: currentAngle,
        finalValue: currentAngle + nodeData.avoidLineAngleOffset,
        runSeconds: CENTER_ANIMATE_ANGLE_TIME,
        decelerationSeconds: DECELERATE_ANGLE_TIME,
      }),
    };
  }

  const selectedChildIndex = indexOfSelectedChild({ children: nodeData.children, selectedNode});
  if (selectedChildIndex === false) return nodeData;

  let targetAngle = 2 * Math.PI - angleBetweenNodes * indexOfSelectedChild({ children: nodeData.children, selectedNode});

  if (currentAngle - targetAngle > 3 * Math.PI) {
    targetAngle = targetAngle + 4 * Math.PI;
  } else if (currentAngle - targetAngle > Math.PI) {
    targetAngle = targetAngle + 2 * Math.PI;
  }

  if (isCenter(nodeData)) {
    targetAngle = targetAngle - offsetOfChildrenToAvoidLine({ nodeData, angleBetweenNodes });
  }

  return {
    ...nodeData,
    angleAnimator: initializeDecelerationPath({
      initialValue: currentAngle,
      finalValue: targetAngle,
      runSeconds: isCenter(nodeData) ? CENTER_ANIMATE_ANGLE_TIME : ANIMATE_ANGLE_TIME,
      decelerationSeconds: DECELERATE_ANGLE_TIME,
    }),
    children: nodeData.children.map(child => setAngles({ nodeData: child, selectedNode }))
  };
}

function targetRadius ({ nodeData, selectedNode }) {
  if (descendantSelected({ nodeData, selectedNodeName: selectedNode.name })) {
    return SELECTED_RADII[nodeData.order];

  } else if (displayingFullSize({ nodeData, selectedNodeName: selectedNode.name })) {
    return DISPLAYING_FULL_SIZE_RADII[nodeData.order];

  } else if (parentDisplayingFullSize({ nodeData, selectedNodeName: selectedNode.name })) {
    return PARENT_DISPLAYING_FULL_SIZE_RADII[nodeData.order];

  } else {
    return UNSELECTED_RADII[nodeData.order];
  }
}

const addFolderNameToFilename = ({ icon, fileName }) =>
  icon ? `node_icons/${fileName}` : `nodes/${fileName}`;

export const isCenter = nodeData => nodeData.order === 0;

const textColor = nodeData => {
  if (!nodeData.desirability) return '#222222';
  if (!nodeData.comparisonDesirability) return Math.abs(nodeData.desirability) <= 3 ? '#222222' : '#ffffff';
  return Math.abs(nodeData.desirability) <= 3 || Math.abs(nodeData.comparisonDesirability) <= 3 ? '#222222' : '#ffffff';
};

const displayingAsIcon = ({ nodeData, selectedNodeName }) => !displayingFullSize({ nodeData, selectedNodeName });

const displayingFullSize = ({ nodeData, selectedNodeName }) =>
  isCenter(nodeData) || (
    parentSelected({ nodeData, selectedNodeName }) ||
    descendantSelected({ nodeData, selectedNodeName }) ||
    (nodeData.order === 3 && siblingSelected({ nodeData, selectedNodeName }))
  );

const parentDisplayingFullSize = ({ nodeData, selectedNodeName }) =>
  displayingFullSize({
    nodeData: {
      nameParts: parentNameParts(nodeData),
      name: parentName(nodeData)
    },
    selectedNodeName
  });

const parentNameParts = nodeData => nodeData.nameParts.slice(0, -1);
const parentName = nodeData => parentNameParts(nodeData).join('.');
const firstOrderAncestorName = nodeData => nodeData.nameParts[0];

const parentSelected = ({ nodeData, selectedNodeName }) =>
  (selectedNodeName === CENTER_NAME && nodeData.order === 1) ||
  parentName(nodeData) === selectedNodeName;

const siblingSelected = ({ nodeData, selectedNodeName }) =>
  nodeData.nameParts.slice(0, -1).join('.') === selectedNodeName.split('.').slice(0, -1).join('.');

const nodeSelected = ({ nodeData, selectedNodeName }) => nodeData.name === selectedNodeName;

// descendant includes self
const descendantSelected = ({ nodeData, selectedNodeName }) =>
  (isCenter(nodeData) && selectedNodeName !== CENTER_NAME) ||
    selectedNodeName?.match(new RegExp(`^${nodeData.name.replaceAll('.', '\\.')}(\\.|$)`));


function nodeImageName ({ nodeData, icon }) {
  if (isCenter(nodeData)) return addFolderNameToFilename({ icon: false, fileName: 'center' });

  let fileName = _nodeImageName ({ desirability: nodeData.desirability, likelihood: nodeData.likelihood, icon });

  if (nodeData.hasComparisonScore) {
    const comparison_file_name = _nodeImageName ({ desirability: nodeData.comparisonDesirability, likelihood: nodeData.comparisonLikelihood, icon });
    if (comparison_file_name !== fileName) {
      fileName = fileName + '_' + comparison_file_name;
    }
  }

  return addFolderNameToFilename({ icon, fileName });
}

function _nodeImageName ({ desirability, likelihood, icon }) {
  const absoluteDesirability = !desirability ? 0 : Math.abs(desirability);
  let fileName = '';

  if (absoluteDesirability <= 3) return 'node';

  if (absoluteDesirability === 50 ) fileName = 'x_';
  if (desirability < 0) fileName = fileName + 'un';
  fileName = fileName + 'desirable';
  if (likelihood >= 7 && !icon) fileName = fileName + '_likely'; // icon nodes do not have a likelihood indicator

  return fileName;
}

const isEven = (n) => n % 2 === 0;
const isOdd = (n) => n % 2 === 1;
// const sign = (v) => (v >= 0) ? 1 : -1;

function offsetOfChildrenToAvoidLine({ nodeData, angleBetweenNodes, parentIsOffset=false }) {
  const angle = Math.min(Math.PI * 0.25, angleBetweenNodes * 0.5);

  if (isOdd(nodeData.children.length)) {
    return 0;
  } else {
    return parentIsOffset ? 0 : -angle;
  }
}

function applyPerspective({ point, screenWidth, screenHalfHeight }) {
  // Thanks to https://math.stackexchange.com/questions/2337183/one-point-perspective-formula#answer-2338025
  const dEyeToScreen = D_EYE_TO_SCREEN; // * BASE_SCREEN_HALF_HEIGHT / screenHalfHeight;
  const baseZ = BASE_Z * Math.pow(BASE_SCREEN_HALF_HEIGHT / screenHalfHeight, 1.4);
  const xInIWheelPlane = point.x;
  const x = xInIWheelPlane * Math.cos(IWHEEL_PLANE_ANGLE);
  const z = xInIWheelPlane * Math.sin(IWHEEL_PLANE_ANGLE) + baseZ;
  const zTotal = z + dEyeToScreen;
  const xPrime = x * dEyeToScreen / zTotal;
  const yPrime = point.y * D_EYE_TO_SCREEN / zTotal;
  const scale = xPrime / x;

  return {
    x: xPrime + screenWidth / 3,
    y: yPrime + screenHalfHeight,
    scale,
  };
}

const makeVector = (point1, point2) => ({ x: point2.x - point1.x, y: point2.y - point1.y });
const vectorMagnitude = vector => Math.sqrt(vector.x * vector.x + vector.y * vector.y);
const vectorAdd = (vector1, vector2) => ({ x: vector1.x + vector2.x, y: vector1.y + vector2.y });
const lineSegment = (vector1, vector2) => [vector1, vector2];

const toUnitVector = vector => {
  const magnitude = vectorMagnitude(vector);
  return { x: vector.x / magnitude, y: vector.y / magnitude };
};

function vectorOfMagnitude ({ vector, magnitude }) {
  const unitVector = toUnitVector(vector);
  return { x: unitVector.x * magnitude, y: unitVector.y * magnitude };
}

const SECOND_ORDER_LINE_OFFSET = 3;
const THIRD_ORDER_LINE_OFFSET = 6;

function generateLineSegments ({ parentPosition, position, nodeData }) {
  if (isCenter(nodeData)) return [];

  let lineSegments = [];
  let parentToChildVector = makeVector(parentPosition, position);
  let counterClockwiseVector = { x: -parentToChildVector.y, y: parentToChildVector.x };
  let clockwiseVector = { x: parentToChildVector.y, y: -parentToChildVector.x };

  if (nodeData.order === 1) {
    lineSegments.push(
      lineSegment(
        parentPosition,
        position,
      )
    );

  } else if (nodeData.order === 2) {
    const counterClockwiseSecondOrderVector = vectorOfMagnitude({ vector: counterClockwiseVector, magnitude: SECOND_ORDER_LINE_OFFSET });
    const clockwiseSecondOrderVector = vectorOfMagnitude({ vector: clockwiseVector, magnitude: SECOND_ORDER_LINE_OFFSET });

    lineSegments.push(
      lineSegment(
        vectorAdd(parentPosition, counterClockwiseSecondOrderVector),
        vectorAdd(position, counterClockwiseSecondOrderVector),
      )
    );

    lineSegments.push(
      lineSegment(
        vectorAdd(parentPosition, clockwiseSecondOrderVector),
        vectorAdd(position, clockwiseSecondOrderVector),
      )
    );

  } else if (nodeData.order === 3) {
    lineSegments.push(
      lineSegment(
        parentPosition,
        position,
      )
    );

    const counterClockwiseSecondOrderVector = vectorOfMagnitude({ vector: counterClockwiseVector, magnitude: THIRD_ORDER_LINE_OFFSET });
    const clockwiseSecondOrderVector = vectorOfMagnitude({ vector: clockwiseVector, magnitude: THIRD_ORDER_LINE_OFFSET });

    lineSegments.push(
      lineSegment(
        vectorAdd(parentPosition, counterClockwiseSecondOrderVector),
        vectorAdd(position, counterClockwiseSecondOrderVector),
      )
    );

    lineSegments.push(
      lineSegment(
        vectorAdd(parentPosition, clockwiseSecondOrderVector),
        vectorAdd(position, clockwiseSecondOrderVector),
      )
    );
  }

  return lineSegments;
}

export function drawNode ({
  drawFunction,
  nodeData,
  selectedNodeName='',
  parentPosition=null,
  parentRotationAngle=0,
  angleBetweenNodes=0,
  initializing=false,
  screenWidth=1400,
  screenHalfHeight=BASE_SCREEN_HALF_HEIGHT,
}) {
  if (!parentPosition) parentPosition = { x: currentDecelerationPathPosition(nodeData.xAnimator), y: 0 };
  const parentChildRadius = currentDecelerationPathPosition(nodeData.radiusToParentAnimator);
  const position = positionRelativeToParent ({ nodeData, parentPosition, parentRotationAngle, parentChildRadius, angleBetweenNodes });
  const { x, y, scale } = applyPerspective({ point: position, screenWidth, screenHalfHeight });

  let params = {
    position: { x, y },
    scale: scale,
    lineSegments: generateLineSegments({ parentPosition, position, nodeData }).map(lineSegment =>
      lineSegment.map(point => applyPerspective({ point, screenWidth, screenHalfHeight }))
    ),
    nodeData,
    isIcon: displayingAsIcon({ nodeData, selectedNodeName }),
  };

  if (initializing) {
    params = {
      ...params,
      nodeImageName: nodeImageName({ nodeData, icon: false }),
      iconNodeImageName: nodeImageName({ nodeData, icon: true }),
      textColor: textColor(nodeData),
    };
  }
  drawFunction(params);

  // angleBetweenNodes: nodeData.children.count ? Math.PI * 2.0 / nodeData.children.count : 0,
  const rotationAngle = currentDecelerationPathPosition(nodeData.angleAnimator) % (2 * Math.PI);

  nodeData.children.forEach(childNodeData => {
    drawNode({
      drawFunction,
      selectedNodeName,
      initializing,
      screenWidth,
      screenHalfHeight,
      nodeData: childNodeData,
      parentPosition: position,
      parentRotationAngle: rotationAngle,
      angleBetweenNodes: angleBetweenNodesForMyChildren(nodeData),
    });
  });
}

function addAngleOffset ({
  nodeData,
  parentAvoidLineAngleOffset=0,
}) {
  const parentIsOffset = (nodeData.order === 1) ? false : !!parentAvoidLineAngleOffset;
  const avoidLineAngleOffset = offsetOfChildrenToAvoidLine({
    nodeData,
    parentIsOffset,
    angleBetweenNodes: angleBetweenNodesForMyChildren(nodeData),
  });

  return {
    ...nodeData,
    avoidLineAngleOffset: isCenter(nodeData) ? avoidLineAngleOffset : parentAvoidLineAngleOffset,
    children: nodeData.children.map(child => addAngleOffset({ nodeData: child, parentAvoidLineAngleOffset: avoidLineAngleOffset })),
  };
}

const toDegrees = radians => radians * 360 / ( 2 * Math.PI);

function positionRelativeToParent ({
  nodeData,
  parentPosition={ x: 0, y: 0 },
  parentRotationAngle=0,
  parentChildRadius=0,
  angleBetweenNodes=0,
}) {
  const angle = (parentRotationAngle + nodeData.index * angleBetweenNodes + nodeData.avoidLineAngleOffset) % (2 * Math.PI);
  // console.log(`parentRotationAngle: ${toDegrees(parentRotationAngle)}; childIndex: ${nodeData.index}; childAngle: ${toDegrees(angle)}`);

  return {
    x: parentPosition.x + Math.cos(angle) * parentChildRadius,
    y: parentPosition.y + Math.sin(angle) * parentChildRadius,
  };
}

export function initializeNode ({ sourceData, parentOrder=-1, index=0, address=[], startingCenterX=null }) {
  // The 5th parameter must be true so that the presence of listeners does
  // not prevent objects from being garbage collected. We use the default
  // values for the 3rd and 4th parameters

  const order = parentOrder + 1;
  const isCenter = order === 0;
  // let angleAnimator;

  // if (order === 0) {
  //   angleAnimator = initializeDecelerationPath({ initialValue: 0, finalValue: 1000 * 1000, runSeconds: 1000 * 1000, decelerationSeconds: DECELERATE_ANGLE_TIME });
  // } else {
  //   angleAnimator = initializeDecelerationPath({ initialValue: 0, finalValue: 0, runSeconds: ANIMATE_ANGLE_TIME, decelerationSeconds: DECELERATE_ANGLE_TIME });
  // }

  let nodeData = {
    index: index,
    name: isCenter ? CENTER_NAME : sourceData.x,
    nameParts: sourceData.x ? sourceData.x.split('.') : [],
    address: address,
    position: sourceData.p,
    id: sourceData.i,
    order: order,

    selected: isCenter,

    angleAnimator: initializeDecelerationPath({ initialValue: 0, finalValue: 0, runSeconds: ANIMATE_ANGLE_TIME, decelerationSeconds: DECELERATE_ANGLE_TIME }),
    radiusToParentAnimator: initializeDecelerationPath({ initialValue: 0, finalValue: UNSELECTED_RADII[order], runSeconds: ANIMATE_RADIUS_TIME, decelerationSeconds: DECELERATE_RADIUS_TIME }),

    text: sourceData.tx,
    flagged: sourceData.flag,
    editable: sourceData.e,
    author: sourceData.a,
    details: sourceData.dtl,

    tags: sourceData.tg,
    delay: sourceData.dl,
    delayComments: sourceData.dlcmnt,
    cumulativeDelay: sourceData.cdl,

    decisionStrategy: sourceData.dstgy,
    decisionStrategyType: sourceData.dstgytp,

    hasScore: !!sourceData.d || sourceData.d === 0,
    desirability: sourceData.d,
    likelihood: sourceData.l,
    minorityReport: sourceData.mreport,

    hasComparisonScore: !!sourceData.cd || sourceData.cd === 0,
    comparisonDesirability: sourceData.cd,
    comparisonLikelihood: sourceData.cl,
    comparisonMinorityReport: sourceData.cmreport,

    children: sourceData.children ?
      sourceData.children.map((childNodeData, childIndex) =>
        initializeNode ({ sourceData: childNodeData, parentOrder: order, index: childIndex, address: address.concat(childIndex) }))
      :
      [],
  };

  nodeData = addAngleOffset({ nodeData });

  if (isCenter) {
    return animateCenterX({ nodeData, startingX: startingCenterX, targetX: CENTER_SELECTED_X });
  } else {
    return nodeData;
  }

}
